import { useState, useEffect } from "react";
import { Card, CardContent } from "components/misc/Cards";
import { PrimaryButton as Button } from "components/misc/Buttons";
import { Download, Eye, Globe, Languages, Wallet, Landmark } from "lucide-react";
import { motion } from "framer-motion";
import tw from "twin.macro";
import styled from "styled-components";

const API_URL = process.env.REACT_APP_IS_PROD == "true" ? process.env.REACT_APP_API_URL_PROD : process.env.REACT_APP_API_URL_DEV


const Section = styled.div`
  ${tw`mt-10`};
  width: 95%;
  margin: auto;
`;

const LargeCard = styled.div`
  ${tw`bg-white rounded-xl shadow-lg p-12 w-full mx-auto flex flex-col lg:flex-row items-start gap-6`};
`;

const Title = styled.h2`
  ${tw`text-xl font-bold mb-6 mx-4 lg:text-3xl text-center lg:text-left`};
`;

const GridContainer = styled.div`
  ${tw`grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 w-full`};
`;

const SmallButton = styled(Button)`
  ${tw`text-xs px-3 py-1`};
`;

const CloseButton = ({ onClick }) => (
  <button
    onClick={onClick}
    style={{
      border: "none",
      fontSize: "30px",
      cursor: "pointer",
      position: "absolute",
      top: "10px",
      right: "10px", // Correctly positions in the top-right corner
      color: "#333",
      background: "transparent" // Removes button background
    }}
    aria-label="Close"
  >
    &times;
  </button>
);


const Modal = tw.div`fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-50`;
const ModalContent = tw.div`bg-white p-4 rounded-lg shadow-lg w-11/12 h-3/4 flex flex-col relative`;

const VisaCard = ({ destinationId }) => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);


  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${API_URL}/web/destinations/visa/?destid=${destinationId}`, {
          headers: {
            'X-API-Key': process.env.REACT_APP_ACCESS_API_KEY // Add API key to headers
          }
        });
        const result = await response.json();

        if (result.header.resCode === 200 && result.data.length > 0) {
          setData(result.data[0]);
        }
      } catch (error) {
        console.error("Error fetching visa data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [destinationId]);

  const handleClose = () => {
    document.body.style.overflow = 'auto';
    setIsModalOpen(false)
  };

  if (isLoading || !data) return null;

  return (
    <>
      <br /><br />
      <Section>
        <Title>Visa Requirement & Other Details</Title>
        <LargeCard>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="w-full">
            <Card className="w-full p-12 sm:p-6 rounded-2xl shadow-lg border">
              <CardContent>
                <GridContainer>
                  <p className="text-gray-700 flex items-center gap-2 text-sm sm:text-base">
                    <Globe className="w-4 sm:w-5 h-4 sm:h-5 text-blue-600" />
                    <strong>Visa Requirement:</strong> {data.visarequirement}
                  </p>
                  <p className="text-gray-700 flex items-center gap-2 text-sm sm:text-base">
                    <Languages className="w-4 sm:w-5 h-4 sm:h-5 text-green-600" />
                    <strong>Language Spoken:</strong> {data.language || "Not Available"}
                  </p>
                  <p className="text-gray-700 flex items-center gap-2 text-sm sm:text-base">
                    <Wallet className="w-4 sm:w-5 h-4 sm:h-5 text-yellow-600" />
                    <strong>Currency:</strong> {data.currency || "Not Available"}
                  </p>
                  <p className="text-gray-700 flex items-center gap-2 text-sm sm:text-base">
                    <Landmark className="w-4 sm:w-5 h-4 sm:h-5 text-red-600" />
                    <strong>Popular Places:</strong> {data.popularplaces ? data.popularplaces.join(", ") : "Not Available"}
                  </p>
                </GridContainer>
              </CardContent>
              <br />
              {data.checklisturl &&
                <div className="flex justify-center mt-4">
                  <SmallButton onClick={() => { setIsModalOpen(true); document.body.style.overflow = 'hidden'; }} variant="outline" className="flex items-center gap-2">
                    <Eye className="w-4 h-4" /> Visa Document Checklist
                  </SmallButton>
                </div>}
            </Card>
          </motion.div>
        </LargeCard>
      </Section>
      {isModalOpen && (
        <Modal>
          <ModalContent>
            <CloseButton onClick={handleClose} />
            <h2>Visa Document Checklist</h2>
            <br />
            {/* PDF Viewer */}
            <iframe
              src={data.checklisturl}
              height={900}
              className="flex-1 w-full h-3/4 border"
              title="Visa Document"
            ></iframe>
            <br />
          </ModalContent>
        </Modal>
      )}
    </>
  );
};

export default VisaCard;